import { Auth } from "aws-amplify";
import { REST_API_BASE_URL } from "../../config/constants";

const TOKEN_EXCHANGE_URL = new URL("/v1/token-exchange", REST_API_BASE_URL).toString();
const STORAGE_KEY = "pentairq_rest_token";

interface StoredToken {
  token: string;
  userSub: string;
  sessionJti: string;  // Amplify JWT session ID — differs on new session
}

// In-memory cache
let cachedToken: string | null = null;
let tokenExchangeInFlight: Promise<string> | null = null;

const getTargetEnvHeader = (): string | null => {
  const stage = process.env.STAGE?.trim();
  return stage ? stage : null;
};

// --- localStorage helpers ---

const saveTokenToStorage = (token: string, userSub: string, sessionJti: string): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ token, userSub, sessionJti } satisfies StoredToken));
  } catch {
    // localStorage unavailable (private browsing, quota exceeded) — silently skip
  }
};

const getStoredToken = (): StoredToken | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as StoredToken;
  } catch {
    return null;
  }
};

export const clearTokenFromLocalStorage = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};

// --- Session helpers ---
const getCurrentUserSub = async (): Promise<string | null> => {
  try {
    const session = await Auth.currentSession();
    return session.getIdToken().payload.sub as string;
  } catch {
    return null;
  }
};

// Decode JWT payload (unsafe, for expiration check only)
const decodeTokenPayload = (token: string): Record<string, any> | null => {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const base64Payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const paddedPayload = base64Payload.padEnd(Math.ceil(base64Payload.length / 4) * 4, "=");
    const payload = JSON.parse(atob(paddedPayload)) as Record<string, any>;
    return payload;
  } catch {
    return null;
  }
};

// Check if token is expired (with 60-second buffer)
const isTokenExpired = (token: string): boolean => {
  const payload = decodeTokenPayload(token);
  if (!payload?.exp) return true;
  const expiresAt = (payload.exp as number) * 1000; // Convert to ms
  const now = Date.now();
  const buffer = 60 * 1000; // 60 second buffer
  return now >= expiresAt - buffer;
};

// Returns true if the stored token belongs to the currently logged-in user and is not expired
export const isTokenValid = async (): Promise<boolean> => {
  const stored = getStoredToken();
  if (!stored) return false;
  if (isTokenExpired(stored.token)) return false;
  const currentSub = await getCurrentUserSub();
  if (!currentSub) return false;
  return stored.userSub === currentSub;
};

// --- Core exchange ---

// Exchange Amplify ID token for REST API token
export const exchangeToken = async (idToken: string): Promise<string> => {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  const targetEnv = getTargetEnvHeader();
  if (targetEnv) {
    headers["X-PQ-Target-Env"] = targetEnv;
  }

  const response = await fetch(TOKEN_EXCHANGE_URL, {
    method: "POST",
    headers,
    body: JSON.stringify({ id_token: idToken }),
  });

  if (!response.ok) {
    throw new Error(`Token exchange failed: ${response.status} ${response.statusText}`);
  }

  const { token } = await response.json();
  return token;
};

const fetchAndCacheToken = async (): Promise<string> => {
  const session = await Auth.currentSession();
  const idToken = session.getIdToken().getJwtToken();
  const userSub = session.getIdToken().payload.sub as string;
  const sessionJti = session.getIdToken().payload.jti as string;

  const exchangedToken = await exchangeToken(idToken);
  cachedToken = exchangedToken;
  saveTokenToStorage(exchangedToken, userSub, sessionJti);

  return exchangedToken;
};

// Initialize token: restore from localStorage if valid (same user, same session), otherwise exchange.
// Detects same user with different session via jti claim and exchanges new token.
export const initializeExchangedToken = async (): Promise<string> => {
  // 1. Fast path: in-memory cache hit
  if (cachedToken) {
    return cachedToken;
  }

  // 2. Check stored token against current session
  const stored = getStoredToken();
  if (stored) {
    const session = await Auth.currentSession();
    const currentSub = session.getIdToken().payload.sub as string;
    const currentJti = session.getIdToken().payload.jti as string;

    if (!currentSub) {
      clearTokenFromLocalStorage();
    } else if (currentSub === stored.userSub && currentJti === stored.sessionJti) {
      // Same user, same session → reuse token
      cachedToken = stored.token;
      return cachedToken;
    } else if (currentSub === stored.userSub && currentJti !== stored.sessionJti) {
      // Same user, different session detected → clear and re-exchange
      clearTokenFromLocalStorage();
    } else {
      // Different user → clear stale storage
      clearTokenFromLocalStorage();
    }
  }

  // 3. New user or new session → perform exchange (deduplicated)
  tokenExchangeInFlight ??= fetchAndCacheToken().finally(() => {
    tokenExchangeInFlight = null;
  });

  return tokenExchangeInFlight;
};

// REST client accessor: validates token before returning; re-exchanges if user changed or expired.
export const getCachedExchangedToken = async (): Promise<string> => {
  if (cachedToken) {
    // Check expiration first (fast, no async needed)
    if (!isTokenExpired(cachedToken)) {
      const valid = await isTokenValid();
      if (valid) return cachedToken;
    }
    // Token is stale (user changed / logged out / expired) — clear and re-exchange
    clearTokenCache();
  }

  return initializeExchangedToken();
};

// Force refresh token if it's expired or invalid
export const refreshTokenIfNeeded = async (): Promise<string> => {
  clearTokenCache();
  return initializeExchangedToken();
};

// Clear in-memory and localStorage caches (call on logout)
export const clearTokenCache = (): void => {
  cachedToken = null;
  tokenExchangeInFlight = null;
  clearTokenFromLocalStorage();
};
